/**
 * Availability Redux Selectors
 */

import { createSelector } from '@spcc/core'
import type { AvailabilityState } from './availability.slice'

// ============================================================================
// Root State Type
// ============================================================================

interface RootState {
  availability: AvailabilityState
}

// ============================================================================
// Base Selector
// ============================================================================

const selectAvailabilityState = (state: RootState) => state.availability

// ============================================================================
// Simple Selectors
// ============================================================================

export const selectFactories = createSelector(
  selectAvailabilityState,
  (state) => state.factories
)

export const selectVariants = createSelector(
  selectAvailabilityState,
  (state) => state.variants
)

export const selectAvailabilityMatrix = createSelector(
  selectAvailabilityState,
  (state) => state.availability
)

export const selectAvailabilityStats = createSelector(
  selectAvailabilityState,
  (state) => state.stats
)

export const selectAvailabilityLoading = createSelector(
  selectAvailabilityState,
  (state) => state.loading
)

export const selectAvailabilityError = createSelector(
  selectAvailabilityState,
  (state) => state.error
)

export const selectAvailabilityFilter = createSelector(
  selectAvailabilityState,
  (state) => state.filter
)

export const selectPendingChanges = createSelector(
  selectAvailabilityState,
  (state) => state.pendingChanges
)

export const selectPendingChangesCount = createSelector(
  selectPendingChanges,
  (changes) => changes.length
)

export const selectHasPendingChanges = createSelector(
  selectPendingChangesCount,
  (count) => count > 0
)

export const selectAvailabilitySaving = createSelector(
  selectAvailabilityState,
  (state) => state.saving
)

export const selectExistingRecords = createSelector(
  selectAvailabilityState,
  (state) => state.records
)

// ============================================================================
// Computed Selectors
// ============================================================================

/**
 * Get availability for a specific factory-variant pair
 */
export const selectIsAvailable = (factoryId: string, variantId: string) =>
  createSelector(selectAvailabilityMatrix, (matrix) => {
    return matrix[factoryId]?.[variantId] ?? false
  })

/**
 * Get all available variants for a factory
 */
export const selectAvailableVariantsForFactory = (factoryId: string) =>
  createSelector([selectVariants, selectAvailabilityMatrix], (variants, matrix) => {
    const factoryAvailability = matrix[factoryId] || {}
    return variants.filter((v) => factoryAvailability[v.documentId] === true)
  })

/**
 * Get all factories where a variant is available
 */
export const selectFactoriesForVariant = (variantId: string) =>
  createSelector([selectFactories, selectAvailabilityMatrix], (factories, matrix) => {
    return factories.filter((f) => matrix[f.documentId]?.[variantId] === true)
  })
